import axios from 'axios';

const DEFAULT_API_BASE_URL = 'https://certs-production-7290.up.railway.app/api';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL;

/** Reads Nest/axios error `response.data.message` (string or validation array) for UI toasts. */
export function getApiErrorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: unknown } }; message?: string };
  const m = e?.response?.data?.message;
  if (Array.isArray(m)) return m.map((x) => String(x)).filter(Boolean).join(' ');
  if (typeof m === 'string' && m.trim()) return m.trim();
  if (e?.message) return String(e.message);
  return 'Request failed';
}

export const getAuthToken = () =>
  localStorage.getItem('access_token') ||
  localStorage.getItem('token') ||
  localStorage.getItem('engineer_token') ||
  localStorage.getItem('client_token') ||
  '';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Important: never force JSON Content-Type for FormData, otherwise the multipart
    // boundary is missing and the backend won't receive the uploaded file.
    const data: any = (config as any).data;
    const isFormData =
      (typeof FormData !== 'undefined' && data instanceof FormData) ||
      (data &&
        typeof data === 'object' &&
        typeof (data as any).append === 'function' &&
        typeof (data as any).entries === 'function');
    if (isFormData) {
      if (config.headers && 'Content-Type' in config.headers) {
        delete (config.headers as any)['Content-Type'];
      }
      if (config.headers && 'content-type' in config.headers) {
        delete (config.headers as any)['content-type'];
      }
    } else {
      const method = String(config.method || 'get').toLowerCase();
      // Avoid non-simple Content-Type on GET/HEAD so some calls skip extra preflight edge cases.
      if (method === 'get' || method === 'head') {
        if (config.headers) {
          delete (config.headers as any)['Content-Type'];
          delete (config.headers as any)['content-type'];
        }
      } else {
        config.headers = config.headers || ({} as any);
        if (!(config.headers as any)['Content-Type'] && !(config.headers as any)['content-type']) {
          (config.headers as any)['Content-Type'] = 'application/json';
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        '';

      const url: string = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/me') || url.includes('/auth/logout');
      const isTokenInvalid =
        String(message).toLowerCase().includes('invalid or expired token') ||
        String(message).toLowerCase().includes('no authentication token');

      if (isAuthEndpoint || isTokenInvalid) {
        const onClientPortal = window.location.pathname.startsWith('/client');
        if (!onClientPortal) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// Dashboard API calls
export const dashboardApi = {
  // Main dashboard endpoint that returns all data
  getDashboard: () => api.get('/dashboard'),
  
  // Individual endpoints for specific data
  getJobs: () => api.get('/jobs'),
  getClients: () => api.get('/clients'),
  getCertificates: () => api.get('/certificates'),
  getInvoices: () => api.get('/invoices'),
  
  // Legacy methods for backward compatibility
  getMetrics: () => api.get('/dashboard'),
  getJobsToday: () => api.get('/dashboard'),
};

// Jobs API calls
export const jobsApi = {
  create: (data: any) => api.post('/jobs', data),
  getAll: () => api.get('/jobs'),
  getById: (id: string) => api.get(`/jobs/${id}`),
  lookup: (q: string) => api.get(`/jobs/lookup?q=${encodeURIComponent(String(q || '').trim())}`),
  update: (id: string, data: any) => api.patch(`/jobs/${id}`, data),
  updateStatus: (id: string, status: string, note?: string) =>
    api.patch(`/jobs/${id}/status`, { status, note }),
  assignEngineer: (id: string, engineerId: string) =>
    api.patch(`/jobs/${id}/assign`, { engineerId }),
  delete: (id: string) => api.delete(`/jobs/${id}`),
  getAvailableEngineers: (scheduledDate: string) =>
    api.get(`/jobs/available-engineers?scheduledDate=${scheduledDate}`),
  suggestEngineers: (scheduledDate: string, durationMinutes?: number) =>
    api.get(`/jobs/suggest-engineers?scheduledDate=${encodeURIComponent(scheduledDate)}${durationMinutes ? `&duration=${durationMinutes}` : ''}`),
};

// Clients API calls
export const clientsApi = {
  create: (data: any) => api.post('/clients', data),
  getAll: () => api.get('/clients'),
  getById: (id: string) => api.get(`/clients/${id}`),
  update: (id: string, data: any) => api.patch(`/clients/${id}`, data),
  delete: (id: string) => api.delete(`/clients/${id}`),
};

// Client portal admin API calls (for inviting clients to portal)
export const clientPortalAdminApi = {
  invite: (clientId: string) => api.post(`/client-portal/admin/invite/${clientId}`),
  listPending: () => api.get(`/client-portal/admin/invites/pending`),
  resend: (clientId: string) => api.post(`/client-portal/admin/invites/${clientId}/resend`),
  revoke: (clientId: string) => api.delete(`/client-portal/admin/invites/${clientId}`),
};

// Sites API calls
export const sitesApi = {
  create: (data: any) => api.post('/sites', data),
  getAll: (clientId?: string) =>
    api.get('/sites', { params: clientId ? { clientId } : {} }),
  getById: (id: string) => api.get(`/sites/${id}`),
  update: (id: string, data: any) => api.patch(`/sites/${id}`, data),
  delete: (id: string) => api.delete(`/sites/${id}`),
};

// Invoices API calls
export const invoicesApi = {
  createFromJob: (data: {
    jobId: string;
    dueInDays?: number;
    notes?: string;
    terms?: string;
    items?: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
      totalPrice?: number;
      itemType?: string;
      sortOrder?: number;
    }>;
  }) => api.post('/invoices', data),
  getAll: (params?: { status?: string; jobId?: string; clientId?: string; limit?: number }) =>
    api.get('/invoices', { params }),
  getStats: () => api.get('/invoices/stats'),
  getById: (id: string) => api.get(`/invoices/${id}`),
  getEvents: (id: string) => api.get(`/invoices/${id}/events`),
  update: (id: string, data: any) => api.put(`/invoices/${id}`, data),
  send: (id: string) => api.post(`/invoices/${id}/send`),
  markPaid: (id: string, data?: { paymentMethod?: string; note?: string }) =>
    api.patch(`/invoices/${id}/mark-paid`, data || {}),
  voidInvoice: (id: string, data?: { note?: string }) =>
    api.patch(`/invoices/${id}/void`, data || {}),
  deleteDraft: (id: string) => api.delete(`/invoices/${id}`),
  getPdfUrl: (id: string) => `${API_BASE_URL}/invoices/${id}/pdf`,
};

// Certificates API calls
export const certificatesApi = {
  create: (data: any) => api.post('/certificates', data),
  getAll: () => api.get('/certificates'),
  getById: (id: string) => api.get(`/certificates/${id}`),
  update: (id: string, data: any) => api.patch(`/certificates/${id}`, data),
  generatePdf: (id: string) => api.post(`/certificates/${id}/generate`),
  extractTemplateFields: (args: {
    file: File;
    certificatePrefix: string;
    certificateTypeName: string;
    templateLabel: string;
  }) => {
    const formData = new FormData();
    formData.append('file', args.file);
    formData.append('certificate_prefix', args.certificatePrefix);
    formData.append('certificate_type_name', args.certificateTypeName);
    formData.append('template_label', args.templateLabel);
    return api.post('/certificates/templates/extract-fields', formData, {
      timeout: 120000,
    });
  },
  inferTemplateSchema: (data: any) =>
    api.post('/certificates/templates/v2/infer-schema', data, {
      timeout: 120000,
    }),
  confirmTemplateSchema: (data: any) =>
    api.post('/certificates/templates/v2/confirm-schema', data, {
      timeout: 120000,
    }),
  listSchemaSnapshotsByImport: (importId: string) =>
    api.get(`/certificates/templates/v2/imports/${encodeURIComponent(importId)}/schemas`),
  listSchemaSnapshotsByTemplate: (templateId: string) =>
    api.get(`/certificates/templates/v2/templates/${encodeURIComponent(templateId)}/schemas`),
  createTemplate: (data: any) => api.post('/certificates/templates', data),
  listTemplates: () => api.get('/certificates/templates'),
  updateTemplate: (id: string, data: any) => api.patch(`/certificates/templates/${encodeURIComponent(id)}`, data),
  delete: (id: string) => api.delete(`/certificates/${id}`),

  // Curated schema library
  listCuratedLibrary: () => api.get('/certificates/templates/curated-library'),
  getCuratedSchema: (key: string) =>
    api.get(`/certificates/templates/curated-library/${encodeURIComponent(key)}`),
  seedCurated: (payload?: { keys?: string[]; overwrite?: boolean }) =>
    api.post('/certificates/templates/seed-curated', payload || {}),

  // Review lifecycle
  listTemplatesPendingReview: () => api.get('/certificates/templates/pending-review'),
  approveTemplate: (id: string, notes?: string) =>
    api.post(`/certificates/templates/${encodeURIComponent(id)}/approve`, { notes }),
  rejectTemplate: (id: string, notes: string) =>
    api.post(`/certificates/templates/${encodeURIComponent(id)}/reject`, { notes }),
  markTemplatePendingReview: (id: string) =>
    api.post(`/certificates/templates/${encodeURIComponent(id)}/mark-pending-review`),
};

export const aiExperimentsApi = {
  pdfFirstInfer: (args: { file: File; certificateTypeCode: string; certificateTypeLabel?: string }) => {
    const formData = new FormData();
    formData.append('file', args.file);
    formData.append('certificateTypeCode', args.certificateTypeCode);
    if (args.certificateTypeLabel) formData.append('certificateTypeLabel', args.certificateTypeLabel);
    return api.post('/ai-experiments/pdf-first/infer', formData, {
      timeout: 240000,
    });
  },
  pdfFirstInferFromExtraction: (args: {
    certificateTypeCode: string;
    certificateTypeLabel?: string;
    pdfStoragePath: string;
    fields: any[];
    pages?: any[];
  }) => api.post('/ai-experiments/pdf-first/infer-from-extraction', args, { timeout: 240000 }),
  pdfFirstLatest: (templateId: string) => api.get(`/ai-experiments/pdf-first/latest/${encodeURIComponent(templateId)}`),
  pdfFirstLatestByType: (certificateTypeCode: string) =>
    api.get(`/ai-experiments/pdf-first/latest-by-type/${encodeURIComponent(certificateTypeCode)}`),
};

export const pdfFirstTemplatesApi = {
  save: (data: any) => api.post('/certificates/templates/pdf-first/save', data, { timeout: 240000 }),
};

// Auth API calls
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  signup: (data: any) => api.post('/auth/signup', data),
  verify: (email: string, code: string) =>
    api.post('/auth/verify', { email, code }),
  resendVerification: (email: string) =>
    api.post('/auth/resend-verification', { email }),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/me'),
};

// Notifications API calls
export const notificationsApi = {
  getAll: (params?: any) => api.get('/notifications', { params }),
  markAsRead: (id: string) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
  handleAction: (id: string, action: string) => 
    api.post(`/notifications/${id}/action`, { action }),
  create: (data: any) => api.post('/notifications', data),
  delete: (id: string) => api.delete(`/notifications/${id}`),
};

// Settings API calls
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data: any) => api.patch('/settings', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post('/settings/change-password', data),
  listTeam: () => api.get('/settings/team'),
  inviteMember: (data: { email: string; role?: string }) =>
    api.post('/settings/team/invite', data),
  updateMemberRole: (userId: string, role: string) =>
    api.patch(`/settings/team/${userId}/role`, { role }),
  updateEngineerProfile: (
    userId: string,
    data: {
      skills?: string[];
      baseAddress?: string | null;
      basePostcode?: string | null;
      serviceRadiusMiles?: number | null;
      maxJobsPerDay?: number | null;
      active?: boolean;
    },
  ) => api.patch(`/settings/team/${userId}/engineer-profile`, data),
  removeMember: (userId: string) => api.delete(`/settings/team/${userId}`),
  cancelInvite: (inviteId: string) => api.delete(`/settings/team/invites/${inviteId}`),
  resendInvite: (inviteId: string) => api.post(`/settings/team/invites/${inviteId}/resend`),
  getMfaStatus: () => api.get('/settings/mfa/status'),
  disableMfa: (factorId: string) => api.delete(`/settings/mfa/${factorId}`),
  uploadProfilePhoto: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/upload/profile-photo', formData);
  },
  uploadCompanyLogo: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/upload/company-logo', formData);
  },
  uploadInvoiceTemplate: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/upload/invoice-template', formData);
  },
  uploadCertificateTemplate: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/upload/certificate-template', formData);
  },
  uploadTermsTemplate: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/upload/terms-template', formData);
  },
};

// Billing (Stripe subscription) API
export const billingApi = {
  config: () => api.get('/billing/config'),
  getSubscription: () => api.get('/billing/subscription'),
  createCheckout: (data: {
    plan: 'SOLO' | 'COMPANY';
    seats?: number;
    successUrl?: string;
    cancelUrl?: string;
  }) => api.post('/billing/checkout', data),
  updateSeats: (seats: number) => api.post('/billing/seats', { seats }),
  cancel: (cancel: boolean) => api.post('/billing/cancel', { cancel }),
  openPortal: (returnUrl?: string) => api.post('/billing/portal', { returnUrl }),
};

// Onboarding wizard API
export const onboardingApi = {
  status: () => api.get('/onboarding/status'),
  saveCompany: (data: any) => api.post('/onboarding/company', data),
  saveProfile: (data: any) => api.post('/onboarding/profile', data),
  saveQuotePolicy: (data: any) => api.post('/onboarding/quote-policy', data),
  complete: () => api.post('/onboarding/complete'),
};

export default api;
